import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import api from "@/lib/api";

const LEVELS = [1, 2, 3, 4, 5];

/**
 * Create/edit form for User records. Extracted from UserManagementPage.jsx per code
 * review (session 149-b) to keep the page file under ~1600 lines and make the form
 * unit-testable in isolation.
 *
 * Props:
 * - dialog: { open, mode: "create"|"edit", data }
 * - setDialog: setter
 * - onSubmit: form submit handler
 * - busy: submit-in-progress flag (disables submit button)
 * - divisions, subDivisions, positions: reference lists
 */
export default function UserFormDialog({ dialog, setDialog, onSubmit, busy, divisions, subDivisions, positions }) {
  const d = dialog.data;
  const set = (patch) => setDialog({ ...dialog, data: { ...d, ...patch } });
  const availableSubs = subDivisions.filter((s) => s.division === d.division);

  // Role Presets — only loaded in create mode. Auto-fills division/level when picked.
  const [presets, setPresets] = useState([]);
  useEffect(() => {
    if (dialog.mode !== "create" || !dialog.open) return;
    let cancelled = false;
    api.get("/role-presets").then(({ data }) => { if (!cancelled) setPresets(data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [dialog.mode, dialog.open]);

  const changeDivision = (v) => {
    const stillValid = subDivisions.some((s) => s.division === v && s.name === d.sub_division);
    set({ division: v, sub_division: stillValid ? d.sub_division : "" });
  };
  const changePreset = (id) => {
    const p = presets.find((x) => x.id === id);
    if (!p) { set({ preset_id: "" }); return; }
    // 2026-06-19: preset custom bisa membawa default sub_division — autofill bila valid.
    const presetSub = p.sub_division && subDivisions.some((s) => s.division === p.division && s.name === p.sub_division) ? p.sub_division : null;
    const stillValid = subDivisions.some((s) => s.division === p.division && s.name === d.sub_division);
    set({ preset_id: id, division: p.division, level: p.level, sub_division: presetSub ?? (stillValid ? d.sub_division : "") });
  };
  const activePreset = presets.find((x) => x.id === d.preset_id);

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
      <DialogContent className="rounded-none max-w-xl" data-testid="dialog-user-form">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{dialog.mode === "create" ? "Add New User" : "Edit User"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {dialog.mode === "create" && (
            <div className="space-y-1.5 p-3 bg-orange-50 border border-orange-200 rounded-none" data-testid="uf-preset-section">
              <Label className="overline text-orange-700 font-semibold">Preset Peran (opsional)</Label>
              {/* 2026-10-09 (user request): standarisasi SearchableSelect utk daftar panjang. */}
              <SearchableSelect
                testid="uf-preset"
                value={d.preset_id || "__none__"}
                onChange={(v) => changePreset(v === "__none__" ? "" : v)}
                options={[{ value: "__none__", label: "— Tanpa preset (isi manual) —" },
                  ...presets.map((p) => ({ value: p.id, label: `${p.label} · ${p.division} Lv${p.level}` }))]}
                placeholder="Pilih preset…"
                searchPlaceholder="Ketik nama preset / divisi…"
                emptyText="Preset tidak ditemukan"
              />
              {activePreset && (
                <div className="text-[10px] text-slate-600 pt-1" data-testid="uf-preset-desc">
                  <div className="italic">{activePreset.description}</div>
                  <div className="mt-0.5 text-slate-500">Akan seed permission matrix untuk <b>{activePreset.feature_count}</b> fitur. Divisi &amp; level di bawah otomatis terisi. Anda bisa ubah sub-divisi &amp; posisi manual.</div>
                </div>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Full Name</Label>
            <Input data-testid="uf-name" required value={d.name} onChange={(e) => set({ name: e.target.value })} className="rounded-none" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Email {dialog.mode === "edit" && <span className="ml-2 text-[10px] text-slate-400">(cannot be changed)</span>}</Label>
            <Input data-testid="uf-email" required type="email" value={d.email} disabled={dialog.mode === "edit"} onChange={(e) => set({ email: e.target.value })} className="rounded-none disabled:bg-slate-100" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Password {dialog.mode === "edit" && <span className="ml-2 text-[10px] text-slate-400">(leave blank to keep current)</span>}</Label>
            <Input data-testid="uf-password" type="text" required={dialog.mode === "create"} value={d.password} onChange={(e) => set({ password: e.target.value })} className="rounded-none font-mono" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Division</Label>
              <SearchableSelect
                testid="uf-division"
                value={d.division}
                onChange={changeDivision}
                options={divisions.map((x) => ({ value: x.name, label: x.name }))}
                placeholder="Pilih…"
                searchPlaceholder="Cari divisi…"
                emptyText="Divisi tidak ditemukan"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Sub Division</Label>
              <Select value={d.sub_division} onValueChange={(v) => set({ sub_division: v })} disabled={!d.division}>
                <SelectTrigger data-testid="uf-subdivision" className="rounded-none">
                  <SelectValue placeholder={!d.division ? "Pilih divisi dulu" : availableSubs.length === 0 ? "Belum ada sub-divisi" : "Pilih…"} />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {availableSubs.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {d.division && availableSubs.length === 0 && (
                <div className="text-[10px] text-amber-600">Tambahkan sub-divisi di tab "Divisions" terlebih dahulu.</div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Level</Label>
              <Select value={String(d.level)} onValueChange={(v) => set({ level: parseInt(v, 10) })}>
                <SelectTrigger data-testid="uf-level" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {LEVELS.map((x) => <SelectItem key={x} value={String(x)}>Lv{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Position</Label>
            <SearchableSelect
              testid="uf-position"
              value={d.position || "__none__"}
              onChange={(v) => set({ position: v === "__none__" ? "" : v })}
              options={[{ value: "__none__", label: "— Tanpa Posisi —" },
                ...positions.map((p) => ({ value: p.name, label: p.name }))]}
              placeholder="Pilih…"
              searchPlaceholder="Cari posisi…"
              emptyText="Posisi tidak ditemukan"
            />
            <div className="text-[10px] text-slate-500">Kelola daftar posisi di tab <span className="font-semibold">Positions</span>.</div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialog({ ...dialog, open: false })}>{ACTION_LABEL.cancel}</Button>
            <Button type="submit" data-testid="uf-submit" disabled={busy || !d.sub_division} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
              {busy ? "Saving…" : (dialog.mode === "create" ? "Create User" : "Save Changes")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
